// CleanBelt Dashboard API Service
// APIエンドポイントへのアクセスを一元管理

import axios from 'axios';
import { API_BASE_URL } from '../constants/config';

// ================================================================================
// ダッシュボード API
// ================================================================================

export const dashboardAPI = {
  getOverview: async (companyId = null) => {
    const response = await axios.get(`${API_BASE_URL}/dashboard/overview`, {
      params: companyId != null ? { company_id: companyId } : {}
    });
    return response.data;
  }
};

// ================================================================================
// 会社管理 API
// ================================================================================

export const companiesAPI = {
  getAll: async () => {
    const response = await axios.get(`${API_BASE_URL}/companies`);
    return response.data;
  },
  
  create: async (companyData) => {
    await axios.post(`${API_BASE_URL}/companies`, companyData);
  },
  
  update: async (companyId, companyData) => {
    await axios.put(`${API_BASE_URL}/companies/${companyId}`, companyData);
  },
  
  delete: async (companyId) => {
    await axios.delete(`${API_BASE_URL}/companies/${companyId}`);
  }
};

// ================================================================================
// 未紐付けイベント API
// ================================================================================

export const pendingEventsAPI = {
  getAll: async (companyId = null) => {
    const response = await axios.get(`${API_BASE_URL}/pending-events`, {
      params: companyId != null ? { company_id: companyId } : {}
    });
    return response.data;
  },
  
  delete: async (pendingEventId) => {
    await axios.delete(`${API_BASE_URL}/pending-events/${pendingEventId}`);
  },
  
  resolve: async (pendingEventId, locationNodeId = null) => {
    const data = locationNodeId ? { location_node_id: locationNodeId } : {};
    await axios.post(`${API_BASE_URL}/pending-events/${pendingEventId}/resolve`, data);
  }
};

// ================================================================================
// 組織階層管理 API
// ================================================================================

export const organizationsAPI = {
  getAll: async (companyId) => {
    const response = await axios.get(`${API_BASE_URL}/companies/${companyId}/organization_units`);
    return response.data;
  },
  
  create: async (companyId, orgData) => {
    await axios.post(`${API_BASE_URL}/companies/${companyId}/organization_units`, orgData);
  },
  
  update: async (orgId, orgData) => {
    await axios.put(`${API_BASE_URL}/organization_units/${orgId}`, orgData);
  },
  
  delete: async (orgId) => {
    await axios.delete(`${API_BASE_URL}/organization_units/${orgId}`);
  }
};

// ================================================================================
// 設置先階層管理 API
// ================================================================================

export const locationsAPI = {
  getAll: async (companyId) => {
    const response = await axios.get(`${API_BASE_URL}/companies/${companyId}/location_nodes`);
    return response.data;
  },
  
  create: async (companyId, locationData) => {
    await axios.post(`${API_BASE_URL}/companies/${companyId}/location_nodes`, locationData);
  },
  
  update: async (locationId, locationData) => {
    await axios.put(`${API_BASE_URL}/location_nodes/${locationId}`, locationData);
  },
  
  delete: async (locationId) => {
    await axios.delete(`${API_BASE_URL}/location_nodes/${locationId}`);
  },
  
  getUnassignedRfids: async (companyId) => {
    const response = await axios.get(`${API_BASE_URL}/companies/${companyId}/unassigned-rfids`);
    return response.data.unassigned_rfids || [];
  }
};

// ================================================================================
// 設置先種別マスタ API
// ================================================================================

export const locationTypesAPI = {
  getAll: async (companyId) => {
    const response = await axios.get(`${API_BASE_URL}/companies/${companyId}/location_node_types`);
    return response.data;
  },
  
  create: async (companyId, typeData) => {
    await axios.post(`${API_BASE_URL}/companies/${companyId}/location_node_types`, typeData);
  },
  
  update: async (typeId, typeData) => {
    await axios.put(`${API_BASE_URL}/location_node_types/${typeId}`, typeData);
  },
  
  delete: async (typeId) => {
    await axios.delete(`${API_BASE_URL}/location_node_types/${typeId}`);
  }
};

// ================================================================================
// 洗浄機管理 API
// ================================================================================

export const washersAPI = {
  getAll: async (companyId) => {
    const response = await axios.get(`${API_BASE_URL}/companies/${companyId}/washers`);
    return response.data;
  },
  
  create: async (companyId, washerData) => {
    await axios.post(`${API_BASE_URL}/companies/${companyId}/washers`, washerData);
  },
  
  update: async (washerId, washerData) => {
    await axios.put(`${API_BASE_URL}/washers/${washerId}`, washerData);
  },
  
  delete: async (washerId) => {
    await axios.delete(`${API_BASE_URL}/washers/${washerId}`);
  },
  
  assign: async (washerId, assignmentData) => {
    await axios.post(`${API_BASE_URL}/washers/${washerId}/assignments`, assignmentData);
  },
  
  unassign: async (assignmentId) => {
    await axios.delete(`${API_BASE_URL}/washer_assignments/${assignmentId}`);
  },
  
  getHistory: async (washerId) => {
    const response = await axios.get(`${API_BASE_URL}/washers/${washerId}/assignments`);
    return response.data;
  }
};

// ================================================================================
// イベントログ API
// ================================================================================

export const eventLogsAPI = {
  getAll: async (filters = {}) => {
    const params = new URLSearchParams();
    
    if (filters.error_only) params.append('error_only', 'true');
    if (filters.event_type && filters.event_type.length > 0) {
      const value = Array.isArray(filters.event_type) ? filters.event_type.join(',') : filters.event_type;
      if (value) params.append('event_type', value);
    }
    if (filters.mac_address) params.append('mac_address', filters.mac_address);
    if (filters.limit) params.append('limit', filters.limit.toString());
    if (filters.offset) params.append('offset', filters.offset.toString());
    if (filters.sort_by) params.append('sort_by', filters.sort_by);
    if (filters.sort_order) params.append('sort_order', filters.sort_order);
    
    const response = await axios.get(`${API_BASE_URL}/sensor-event-logs?${params.toString()}`);
    return response.data;
  },
  
  delete: async (eventLogId) => {
    await axios.delete(`${API_BASE_URL}/sensor-event-logs/${eventLogId}`);
  },
  
  reprocess: async (eventLogId) => {
    await axios.post(`${API_BASE_URL}/sensor-event-logs/${eventLogId}/reprocess`);
  }
};
